import type { ScreenInventoryItem } from './screen-inventory.types';

const MAX_ITEMS = 40;
const MAX_TEXT_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 300;

const LEAD_IN = 'The list below describes what is on the user\'s screen right now. It is rebuilt '
    + 'for every message, so it replaces any earlier list. Refer to an item by its id. It says '
    + 'what each item contains; it does not include the values.';

/** Customer text goes inside quotes, so it must not be able to close the quote or the block. */
export function safeText (text: string): string {
    return String(text).replace(/[\r\n<>]/g, ' ').replace(/"/g, '\'').replace(/\s+/g, ' ').trim();
}

function truncate (text: string, length: number): string {
    return text.length > length ? `${text.slice(0, length)}…` : text;
}

function quote (text: string, length = MAX_TEXT_LENGTH): string {
    return `"${truncate(safeText(text), length)}"`;
}

function formatPage (routeName: string, pageTitle: string): string {
    const title = pageTitle ? `${quote(pageTitle)} ` : '';

    return `page: ${title}(route ${routeName || 'unknown'})`;
}

function formatLabels (item: ScreenInventoryItem): string {
    if (!item.labels.length) return '';

    const names = item.labels.map((label) => (label.hidden ? `${quote(label.text)} (hidden)` : quote(label.text)));
    const more = item.labelTotal - item.labels.length;
    if (more > 0) names.push(`+${more} more`);

    return `; ${item.labelsOf}: ${names.join(', ')}`;
}

function formatItem (item: ScreenInventoryItem): string {
    const parts = [`- ${item.kind}`];

    if (item.title) parts.push(quote(item.title));
    parts.push(`[id: ${item.id}]`, item.shape);

    let line = parts.join(' ');
    for (const detail of item.details ?? []) line += `; ${detail.name} ${quote(detail.text)}`;
    line += formatLabels(item);
    if (!item.visible) line += ' (not visible)';
    if (item.description) line += `\n  description: ${quote(item.description, MAX_DESCRIPTION_LENGTH)}`;

    return line;
}

export function serializeScreenInventory (items: ScreenInventoryItem[], { routeName, pageTitle }: { routeName: string; pageTitle: string }): string {
    const kept = items.slice(0, MAX_ITEMS);
    const lines = [formatPage(routeName, pageTitle), ...kept.map(formatItem)];

    if (!items.length) lines.push('- nothing to list');
    if (items.length > kept.length) lines.push(`- +${items.length - kept.length} more items not listed`);

    return `${LEAD_IN}\n<on-screen>\n${lines.join('\n')}\n</on-screen>`;
}
